// Combined /workspace/prices page: AI-assisted upload + saved formats + history.
//
// Flow:
//   1. Operator drops an xlsx file.
//   2. /analyze runs structural detection + (unless skip_ai) calls the LLM.
//   3. Detector + AI proposal render in a two-column layout; operator can
//      tweak the resolved rules inline (column overrides, default values).
//   4. /commit-ai applies the rules and creates draft/ready products.
//   5. Optional "Save as format" persists the rules so the next upload of
//      the same shape skips the AI call entirely.
//
// The saved-formats list and history are collapsible under the main flow.

import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  priceAiApi,
  priceImportsApi,
  sellersApi,
  type AnalyzeResponse,
  type CommitAiResponse,
  type PriceFormat,
  type PriceImport,
  type SellerListItem,
} from '../lib/api';
import { useAuth } from '../lib/auth';
import { useBasePath, withBase } from '../lib/basePath';

const REQUIRED_FIELDS = ['name', 'price_rub', 'bunch_size', 'min_order_count', 'height_cm', 'external_offer_id'] as const;

const FIELD_LABELS: Record<string, string> = {
  name:              'Название',
  price_rub:         'Цена ₽',
  bunch_size:        'Кол-во в упаковке',
  min_order_count:   'Минимум',
  height_cm:         'Высота, см',
  stock_qty:         'Наличие',
  external_offer_id: 'Артикул',
  photo_url:         'Фото (URL)',
  box_qty:           'В коробке',
  country:           'Страна',
  color:             'Цвет',
};

const WARNING_LABELS: Record<string, string> = {
  no_price_column:     'Нет колонки с ценой — будет загружен как каталог (товары → draft)',
  no_stock_column:     'Нет колонки «Наличие». Возможно, нужно указать через расписание',
  no_pack_size_column: 'Нет колонки «Количество в упаковке»',
  no_min_order_column: 'Нет колонки «Минимум»',
  no_height_column:    'Нет колонки «Высота» — возможно вшита в название или в «Качество»',
  pivot_values_uncertain: 'Ячейки в pivot-сетке: цены или количества? Подтверди.',
  needs_companion_price: 'Похоже на компаньон-файл (квант, без цен). Прикрепи основной прайс.',
};

export default function PriceWorkspace() {
  const { user } = useAuth();
  const base = useBasePath();
  const fileRef = useRef<HTMLInputElement>(null);
  const [searchParams] = useSearchParams();

  const [sellers, setSellers]   = useState<SellerListItem[]>([]);
  const [sellerId, setSellerId] = useState<number | ''>(() => {
    const fromUrl = searchParams.get('seller_id');
    return fromUrl ? Number(fromUrl) : '';
  });

  const [file, setFile]               = useState<File | null>(null);
  const [analyzing, setAnalyzing]     = useState(false);
  const [committing, setCommitting]   = useState(false);
  const [analyzed, setAnalyzed]       = useState<AnalyzeResponse | null>(null);
  const [committed, setCommitted]     = useState<CommitAiResponse | null>(null);
  const [err, setErr]                 = useState<string | null>(null);

  // Editable rules — start from the AI analysis, operator can tweak before commit.
  const [overrides, setOverrides]     = useState<Record<string, { col?: number; value?: number | string }>>({});
  const [defaults, setDefaults]       = useState<Record<string, number | string>>({});
  const [formatName, setFormatName]   = useState('');

  const [formats, setFormats] = useState<PriceFormat[]>([]);
  const [history, setHistory] = useState<PriceImport[]>([]);
  const [showFormats, setShowFormats] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Companion-file flow: after a successful import, operator can attach a
  // second file (e.g. KCH квант to KCH прайс). The companion's import row
  // links back to the primary via companion_of_id; full automatic merge
  // by (name + height) is a v2 — for now we just track the relationship.
  const [companionOfId, setCompanionOfId] = useState<number | null>(null);

  // AI Q&A: operator picks one of the suggested options OR types free-form
  // text for each open question. On "Уточнить" we re-call /analyze with
  // chat_history so the model can revise its mapping with the new context.
  const [answers, setAnswers]           = useState<Record<string, string>>({});
  const [otherOpen, setOtherOpen]       = useState<Record<string, boolean>>({});
  const [refining, setRefining]         = useState(false);

  // Accumulated multi-turn conversation with the AI. Each "Уточнить" appends
  // the assistant's previous JSON response + the new operator turn, so the
  // next call has full context (the model remembers its own past decisions,
  // not just the latest operator message).
  const [aiConversation, setAiConversation] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([]);

  // Snapshot of the previous AI proposal — kept so the mapping/warnings UI can
  // highlight which fields the latest "Уточнить" actually changed. Cleared on
  // new upload / commit / explicit "Скрыть изменения".
  const [previousAnalysis, setPreviousAnalysis] = useState<AnalyzeResponse | null>(null);

  // Awaited diff payload for the "Показать изменения" modal in the history
  // table. Null = modal closed; populated = open with that import's diffs.
  type DiffPayload = Awaited<ReturnType<typeof priceAiApi.diffs>>;
  const [diffModal, setDiffModal] = useState<DiffPayload | null>(null);
  const [diffLoading, setDiffLoading] = useState<number | null>(null);

  async function openDiff(importId: number) {
    setDiffLoading(importId);
    try {
      const r = await priceAiApi.diffs(importId);
      setDiffModal(r);
    } catch (e) {
      alert(`Не удалось загрузить изменения: ${String((e as Error)?.message ?? e)}`);
    } finally {
      setDiffLoading(null);
    }
  }

  const effectiveSellerId = user?.role === 'seller' ? user.id
                          : typeof sellerId === 'number' ? sellerId : null;

  useEffect(() => {
    if (user?.role === 'admin') {
      sellersApi.list().then((r) => setSellers(r.sellers)).catch(() => { /* noop */ });
    }
  }, [user]);

  useEffect(() => {
    if (effectiveSellerId == null) { setFormats([]); setHistory([]); return; }
    priceAiApi.formats.list(effectiveSellerId).then((r) => setFormats(r.formats)).catch(() => setFormats([]));
    priceImportsApi.list(effectiveSellerId).then((r) => setHistory(r.imports)).catch(() => setHistory([]));
  }, [effectiveSellerId]);

  async function onAnalyze(opts: { skipAi?: boolean } = {}) {
    if (!file) { setErr('Выбери файл'); return; }
    if (effectiveSellerId == null) { setErr('Выбери продавца'); return; }
    setAnalyzing(true);
    setErr(null);
    setCommitted(null);
    try {
      const r = await priceAiApi.analyze(file, {
        seller_id: effectiveSellerId,
        skip_ai: opts.skipAi,
      });
      setAnalyzed(r);
      setOverrides({});
      setDefaults({});
      setAnswers({});
      setOtherOpen({});
      setAiConversation([]);
      setFormatName(r.format_match?.name || guessFormatName(file.name));
    } catch (e) {
      setErr(String((e as Error)?.message ?? e));
    } finally { setAnalyzing(false); }
  }

  // Send accumulated answers back to AI for a refined proposal. The previous
  // questions + answers are formatted into a single user turn so the model
  // sees what the operator clarified.
  async function onRefineWithAnswers() {
    if (!file || !analyzed || effectiveSellerId == null) return;
    const questions = analyzed.analysis?.questions ?? [];
    const filled = questions.filter((q) => answers[q.id]?.trim());
    if (filled.length === 0) { setErr('Ответь хотя бы на один вопрос или нажми «Сбросить ответы»'); return; }

    const userTurn = [
      'Ответы на твои предыдущие вопросы:',
      ...filled.map((q) => `- Вопрос: ${q.ask}\n  Ответ: ${answers[q.id].trim()}`),
      '',
      'Пересобери mapping с учётом этих ответов. Если каких-то полей всё ещё не хватает — задай новые вопросы.',
    ].join('\n');

    setRefining(true);
    setErr(null);
    // Snapshot the current proposal BEFORE replacing it so the next render
    // can compute "what changed" indicators in the mapping/warnings UI.
    setPreviousAnalysis(analyzed);
    // Build the assistant turn from the current analysis JSON. The model
    // sees exactly the structured output it produced last time, then the
    // operator's new clarifications.
    const assistantTurn = {
      role: 'assistant' as const,
      content: analyzed.analysis ? JSON.stringify(analyzed.analysis) : (analyzed.ai_raw ?? ''),
    };
    const userMessage = { role: 'user' as const, content: userTurn };
    const newHistory  = [...aiConversation, assistantTurn, userMessage];
    try {
      const r = await priceAiApi.analyze(file, {
        seller_id: effectiveSellerId,
        chat_history: newHistory,
      });
      setAnalyzed(r);
      setAiConversation(newHistory);
      // Preserve user's local overrides + defaults, but reset answers for
      // the NEW question set (ids likely differ).
      setAnswers({});
      setOtherOpen({});
    } catch (e) {
      setErr(String((e as Error)?.message ?? e));
    } finally { setRefining(false); }
  }

  async function onCommit({ saveFormat }: { saveFormat: boolean }) {
    if (!file || !analyzed || effectiveSellerId == null) return;
    const rules = buildRules(analyzed, overrides, defaults);
    if (!rules.table_type) { setErr('Не определился тип таблицы'); return; }
    setCommitting(true);
    setErr(null);
    try {
      const r = await priceAiApi.commit(file, {
        seller_id: effectiveSellerId,
        rules,
        save_as_format_name: saveFormat ? formatName : undefined,
        companion_of_id: companionOfId ?? undefined,
      });
      setCommitted(r);
      setPreviousAnalysis(null);
      setAiConversation([]);
      // Refresh sidebars
      priceAiApi.formats.list(effectiveSellerId).then((x) => setFormats(x.formats)).catch(() => {});
      priceImportsApi.list(effectiveSellerId).then((x) => setHistory(x.imports)).catch(() => {});
    } catch (e) {
      setErr(String((e as Error)?.message ?? e));
    } finally { setCommitting(false); }
  }

  function resetUpload(opts: { keepCompanion?: boolean } = {}) {
    setFile(null);
    setAnalyzed(null);
    setCommitted(null);
    setErr(null);
    setOverrides({});
    setDefaults({});
    if (!opts.keepCompanion) setCompanionOfId(null);
    setPreviousAnalysis(null);
    setAiConversation([]);
    if (fileRef.current) fileRef.current.value = '';
  }

  const mapping = analyzed?.analysis?.mapping ?? {};
  const mergedFields = mergeFieldGuesses(analyzed);
  const warnings = analyzed?.analysis?.warnings ?? analyzed?.detector.warnings ?? [];
  const fieldDiffs = diffMappings(previousAnalysis, analyzed);
  const warningDiffs = diffWarnings(
    previousAnalysis?.analysis?.warnings ?? previousAnalysis?.detector.warnings ?? null,
    warnings,
  );
  const changedFieldCount = Object.values(fieldDiffs).filter((d) => d !== 'same').length;

  return (
    <div className="container">
      <div className="page-head">
        <h2>Прайсы</h2>
        <span className="muted" style={{ fontSize: 12 }}>
          AI-импорт прайс-листов: детектор находит структуру, агент уточняет, ты подтверждаешь.
        </span>
      </div>

      {/* --- Seller picker (admin) --- */}
      {user?.role === 'admin' && (
        <div className="card form" style={{ marginBottom: 12 }}>
          <label>
            <span>Продавец</span>
            <select value={sellerId} onChange={(e) => { setSellerId(e.target.value ? Number(e.target.value) : ''); resetUpload(); }}>
              <option value="">— выберите —</option>
              {sellers.map((s) => <option key={s.id} value={s.id}>{s.display_name || s.login}</option>)}
            </select>
          </label>
        </div>
      )}

      {/* --- Upload zone --- */}
      {!analyzed && !committed && (
        <div className="card" style={{ padding: 24 }}>
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <h3 style={{ marginTop: 0, marginBottom: 0 }}>Загрузить новый прайс</h3>
            <div className="row" style={{ gap: 6 }}>
              <span className="muted" style={{ fontSize: 12 }}>Скачать шаблон:</span>
              <a className="btn" href="/api/price-imports/template/flat">Flat</a>
              <a className="btn" href="/api/price-imports/template/pivot">Pivot</a>
              <a className="btn" href="/api/price-imports/template/catalog">Каталог</a>
            </div>
          </div>
          <p className="muted">
            Загружаешь xlsx — система пытается распознать структуру, маппинг колонок и форму отгрузки.
            Если у нас уже есть сохранённый формат с такой же шапкой — AI не вызывается, всё проходит мгновенно и бесплатно.
          </p>
          <div
            className="upload-zone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files?.[0];
              if (f) setFile(f);
            }}
            style={{
              border: '2px dashed #c8d2cc',
              borderRadius: 12,
              padding: 32,
              textAlign: 'center',
              marginTop: 12,
              cursor: 'pointer',
              background: file ? '#eef3ec' : '#f7f6f2',
            }}
            onClick={() => fileRef.current?.click()}
          >
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              style={{ display: 'none' }}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            {file ? (
              <>
                <strong>{file.name}</strong>
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{(file.size / 1024).toFixed(0)} KB</div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 18 }}>Перетащи xlsx сюда или кликни, чтобы выбрать</div>
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>до 50 MB, .xlsx / .xls / .csv</div>
              </>
            )}
          </div>

          <div className="row" style={{ marginTop: 12, gap: 8 }}>
            <button
              className="btn primary"
              type="button"
              disabled={!file || analyzing || effectiveSellerId == null}
              onClick={() => onAnalyze()}
            >{analyzing ? 'Анализирую…' : 'Анализировать'}</button>
            <button
              className="btn"
              type="button"
              disabled={!file || analyzing}
              onClick={() => onAnalyze({ skipAi: true })}
              title="Только структурный детектор, без AI — быстро и бесплатно"
            >Без AI (детектор)</button>
            {file && <button className="btn" onClick={() => resetUpload()}>Сбросить</button>}
          </div>

          {err && <div className="error" style={{ marginTop: 12 }}>{err}</div>}
        </div>
      )}

      {/* --- Analysis result --- */}
      {analyzed && !committed && (
        <div className="card" style={{ marginTop: 12 }}>
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h3 style={{ margin: 0 }}>
                {analyzed.detector.table_type === 'flat'    && 'Flat прайс'}
                {analyzed.detector.table_type === 'pivot'   && 'Pivot прайс (сорт × высоты)'}
                {analyzed.detector.table_type === 'catalog' && 'Каталог без цен'}
                {analyzed.detector.table_type === 'matrix'  && 'Companion-файл (квант/матрица)'}
              </h3>
              <p className="muted" style={{ marginTop: 4 }}>
                Лист «{analyzed.detector.sheet_name}». Строк данных: ≈{analyzed.detector.total_data_rows}. Header row: {analyzed.detector.header_row}.
                {analyzed.detector.embedded_images_count > 0 && ` · ${analyzed.detector.embedded_images_count} фото внутри файла`}
              </p>
            </div>
            <button className="btn" type="button" onClick={() => resetUpload()}>← Новая загрузка</button>
          </div>

          {/* AI summary + chat */}
          {analyzed.analysis?.summary && (
            <div className="ai-summary" style={{ background: '#eef3ec', padding: 12, borderRadius: 10, marginTop: 10 }}>
              <strong>AI:</strong> {analyzed.analysis.summary}
              <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                ${(analyzed.ai_cost_cents / 100).toFixed(4)} (in {analyzed.ai_input_tokens}, out {analyzed.ai_output_tokens})
              </div>
            </div>
          )}
          {analyzed.ai_error && (
            <div className="card sub" style={{ marginTop: 10, borderLeft: '4px solid #c0392b' }}>
              <strong style={{ color: '#a52e22' }}>AI не вернул корректный JSON.</strong>
              <p className="muted" style={{ marginTop: 4 }}>
                Маппинг ниже построен только структурным детектором (без AI). Можешь поправить его руками
                и применить, либо нажать «Анализировать» ещё раз — иногда модель отвечает корректно со второй попытки.
              </p>
              {analyzed.ai_raw && (
                <details style={{ marginTop: 6 }}>
                  <summary style={{ cursor: 'pointer', fontSize: 12 }}>Показать сырой ответ модели</summary>
                  <pre style={{ whiteSpace: 'pre-wrap', fontSize: 11, maxHeight: 240, overflow: 'auto', background: '#f7f6f2', padding: 8, borderRadius: 6, marginTop: 6 }}>
                    {analyzed.ai_raw}
                  </pre>
                </details>
              )}
            </div>
          )}
          {previousAnalysis && (
            <div className="badge ok" style={{ marginTop: 8 }}>
              ✨ После уточнения изменилось полей: {changedFieldCount}
              {warningDiffs.removed.length > 0 && ` · убрано предупреждений: ${warningDiffs.removed.length}`}
              {warningDiffs.added.length > 0 && ` · новых: ${warningDiffs.added.length}`}
              {' · '}
              <button className="link-btn" type="button" onClick={() => setPreviousAnalysis(null)}>скрыть отметки</button>
            </div>
          )}
          {analyzed.format_match && (
            <div className="badge ok" style={{ marginTop: 10 }}>
              Найден сохранённый формат «{analyzed.format_match.name}» — AI пропущен, бесплатно
            </div>
          )}

          {warnings.length > 0 && (
            <div className="warnings" style={{ marginTop: 12 }}>
              {warnings.map((w) => {
                const isNew = warningDiffs.added.includes(w);
                return (
                  <div key={w} className="badge warn" style={{ display: 'block', marginBottom: 4 }}>
                    ⚠ {WARNING_LABELS[w] || w}
                    {isNew && <span style={{ marginLeft: 6, fontWeight: 700, color: '#1F3D34' }}>(новое)</span>}
                  </div>
                );
              })}
              {warningDiffs.removed.length > 0 && warningDiffs.removed.map((w) => (
                <div key={w} className="badge ok" style={{ display: 'block', marginBottom: 4, opacity: 0.7 }}>
                  ✓ Снято: {WARNING_LABELS[w] || w}
                </div>
              ))}
            </div>
          )}

          {/* AI follow-up questions */}
          {analyzed.analysis?.questions && analyzed.analysis.questions.length > 0 && (
            <div className="card sub" style={{ marginTop: 12 }}>
              <strong>Открытые вопросы от AI:</strong>
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 12 }}>
                {analyzed.analysis.questions.map((q) => {
                  const selected = answers[q.id] ?? '';
                  const isOtherOpen = !!otherOpen[q.id];
                  const isCustom = selected && !q.options?.includes(selected);
                  return (
                    <div key={q.id} style={{ background: '#f7f6f2', padding: 10, borderRadius: 8 }}>
                      <div style={{ marginBottom: 8 }}>❓ {q.ask}</div>
                      <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                        {(q.options ?? []).map((opt) => {
                          const active = selected === opt;
                          return (
                            <button
                              key={opt}
                              type="button"
                              className={`btn ${active ? 'primary' : ''}`}
                              onClick={() => {
                                setAnswers((p) => ({ ...p, [q.id]: active ? '' : opt }));
                                setOtherOpen((p) => ({ ...p, [q.id]: false }));
                              }}
                              style={{ fontSize: 12 }}
                            >{active ? '✓ ' : ''}{opt}</button>
                          );
                        })}
                        <button
                          type="button"
                          className={`btn ${isOtherOpen || isCustom ? 'primary' : ''}`}
                          onClick={() => setOtherOpen((p) => ({ ...p, [q.id]: !p[q.id] }))}
                          style={{ fontSize: 12 }}
                        >{isCustom ? `✓ Свой: ${selected.slice(0, 24)}${selected.length > 24 ? '…' : ''}` : 'Другое…'}</button>
                        {selected && (
                          <button
                            type="button"
                            className="link-btn"
                            onClick={() => {
                              setAnswers((p) => { const n = { ...p }; delete n[q.id]; return n; });
                              setOtherOpen((p) => ({ ...p, [q.id]: false }));
                            }}
                            style={{ fontSize: 11 }}
                          >× сброс</button>
                        )}
                      </div>
                      {isOtherOpen && (
                        <textarea
                          value={isCustom ? selected : ''}
                          onChange={(e) => setAnswers((p) => ({ ...p, [q.id]: e.target.value }))}
                          placeholder="Напиши свой ответ для AI…"
                          rows={2}
                          style={{ width: '100%', marginTop: 8, fontSize: 12, padding: 6 }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="row" style={{ marginTop: 12, gap: 8 }}>
                <button
                  className="btn primary"
                  type="button"
                  disabled={refining || Object.values(answers).every((v) => !v?.trim())}
                  onClick={onRefineWithAnswers}
                >{refining ? 'AI пересобирает…' : 'Уточнить с этими ответами'}</button>
                <button
                  className="btn"
                  type="button"
                  disabled={refining || Object.keys(answers).length === 0}
                  onClick={() => { setAnswers({}); setOtherOpen({}); }}
                >Сбросить ответы</button>
                <span className="muted" style={{ fontSize: 11, alignSelf: 'center' }}>
                  {aiConversation.length === 0
                    ? 'Первое уточнение. Каждый круг — отдельный вызов AI (~$0.02-0.05).'
                    : `Круг ${aiConversation.length / 2 + 1}. AI помнит ${aiConversation.length / 2} предыдущих ответ${aiConversation.length === 2 ? '' : 'а'}.`}
                </span>
              </div>
            </div>
          )}

          {/* Mapping table */}
          <h4 style={{ marginTop: 16, marginBottom: 6 }}>Маппинг полей</h4>
          <table className="grid compact">
            <thead>
              <tr>
                <th>Поле</th>
                <th>Источник</th>
                <th>Колонка / значение</th>
                <th>Уверенность</th>
                <th>Дефолт (если пусто)</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(mergedFields).map(([field, info]) => {
                const required = REQUIRED_FIELDS.includes(field as never);
                const diff = fieldDiffs[field] || 'same';
                const rowStyle =
                  diff === 'changed' ? { background: '#fff7e6' } :
                  diff === 'new'     ? { background: '#eef3ec' } :
                  diff === 'removed' ? { background: '#f7e6e6', opacity: 0.65 } : undefined;
                return (
                  <tr key={field} style={rowStyle}>
                    <td>
                      <strong>{FIELD_LABELS[field] || field}</strong>
                      {required && <span className="muted" style={{ fontSize: 10 }}> · обяз.</span>}
                      {diff === 'changed' && <span className="badge ok" style={{ marginLeft: 6, fontSize: 10 }}>✎ изменено</span>}
                      {diff === 'new'     && <span className="badge ok" style={{ marginLeft: 6, fontSize: 10 }}>＋ новое</span>}
                      {diff === 'removed' && <span className="badge warn" style={{ marginLeft: 6, fontSize: 10 }}>− убрано</span>}
                    </td>
                    <td>
                      <code style={{ fontSize: 11 }}>{info.source}</code>
                    </td>
                    <td>
                      {info.col != null ? (
                        <span>col {info.col} · {analyzed.detector.headers[info.col] || ''}</span>
                      ) : info.source === 'literal' ? (
                        <span>{String(info.value ?? '—')}</span>
                      ) : info.source === 'pivot_value' || info.source === 'pivot_column_label' ? (
                        <span className="muted">из pivot-сетки</span>
                      ) : info.source === 'derived' ? (
                        <span className="muted">авто-генерация</span>
                      ) : (
                        <span className="muted">не определено</span>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${(info.confidence ?? 0) > 0.7 ? 'ok' : 'warn'}`}>
                        {((info.confidence ?? 0) * 100).toFixed(0)}%
                      </span>
                    </td>
                    <td>
                      {(info.confidence ?? 0) < 0.7 || info.col == null ? (
                        <input
                          type={field === 'price_rub' || field.endsWith('_cm') || field === 'bunch_size' || field === 'min_order_count' || field === 'stock_qty' ? 'number' : 'text'}
                          value={defaults[field] ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setDefaults((p) => ({ ...p, [field]: v === '' ? '' : (Number.isFinite(Number(v)) ? Number(v) : v) }));
                          }}
                          placeholder={field === 'min_order_count' ? '1' : '—'}
                          style={{ width: 80, fontSize: 12, padding: '2px 6px' }}
                        />
                      ) : <span className="muted">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Pivot value columns */}
          {analyzed.detector.value_columns.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <strong>Pivot-колонки (высоты):</strong>{' '}
              {analyzed.detector.value_columns.map((v) => (
                <span key={v.col} className="badge ok" style={{ marginRight: 4 }}>
                  {v.label}{v.parsed ? ` (${v.parsed}cm)` : ''}
                </span>
              ))}
            </div>
          )}

          {/* Section forward-fill */}
          {analyzed.detector.section_count > 0 && (
            <div style={{ marginTop: 8 }}>
              <strong>Секции (forward-fill в категорию):</strong>{' '}
              {analyzed.detector.section_rows.slice(0, 8).map((s) => (
                <span key={s.row} className="badge" style={{ marginRight: 4 }}>{s.text}</span>
              ))}
              {analyzed.detector.section_rows.length > 8 && <span className="muted">+{analyzed.detector.section_rows.length - 8} ещё…</span>}
            </div>
          )}

          {/* Preview rows */}
          {analyzed.detector.preview_rows.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <strong>Образец строк:</strong>
              <div style={{ overflowX: 'auto', marginTop: 6 }}>
                <table className="grid compact">
                  <thead>
                    <tr>
                      <th>#</th>
                      {Object.entries(analyzed.detector.headers).map(([col, txt]) => (
                        <th key={col} style={{ minWidth: 60 }}>{txt}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {analyzed.detector.preview_rows.slice(0, 8).map((r) => (
                      <tr key={r.row}>
                        <td className="muted">{r.row}</td>
                        {Object.keys(analyzed.detector.headers).map((col) => (
                          <td key={col} style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {r.cells[col] || <span className="muted">—</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Commit / save */}
          <div className="card sub" style={{ marginTop: 16 }}>
            <label>
              <span>Имя для сохранения формата (необязательно)</span>
              <input
                type="text"
                value={formatName}
                onChange={(e) => setFormatName(e.target.value)}
                placeholder="Например: STEMS поставка"
                style={{ width: 320 }}
              />
            </label>
            <div className="row" style={{ marginTop: 10, gap: 8 }}>
              <button
                className="btn primary"
                type="button"
                disabled={committing}
                onClick={() => onCommit({ saveFormat: false })}
              >{committing ? 'Заливаем…' : 'Применить как черновики'}</button>
              <button
                className="btn primary"
                type="button"
                disabled={committing || !formatName.trim()}
                onClick={() => onCommit({ saveFormat: true })}
                title="После сохранения формата следующая загрузка такого же файла пройдёт без AI"
              >{committing ? '…' : 'Применить + сохранить формат'}</button>
            </div>
            {err && <div className="error" style={{ marginTop: 10 }}>{err}</div>}
          </div>
        </div>
      )}

      {/* --- Commit result --- */}
      {committed && (
        <div className="card sub" style={{ marginTop: 18 }}>
          <h3 style={{ marginTop: 0 }}>Импорт завершён</h3>
          <p>
            Создано: <strong>{committed.inserted}</strong>{' · '}
            Обновлено: <strong>{committed.updated}</strong>{' · '}
            Пропущено: <strong>{committed.skipped}</strong> из {committed.total_candidates} кандидатов
          </p>
          <p>
            Всего у продавца товаров: готовых <strong>{committed.seller_ready_total}</strong>,
            черновиков <strong>{committed.seller_draft_total}</strong>.
          </p>
          {committed.format_id && <p className="badge ok">Формат сохранён (id {committed.format_id}) — следующая такая загрузка пройдёт без AI</p>}
          {committed.photo_stats && (committed.photo_stats.saved > 0 || committed.photo_stats.skipped > 0) && (
            <p>
              Фото: загружено <strong>{committed.photo_stats.saved}</strong>
              {committed.photo_stats.skipped > 0 && <>, пропущено <strong>{committed.photo_stats.skipped}</strong> (нет источника или ошибка скачивания)</>}.
            </p>
          )}
          {!!committed.sku_collisions && committed.sku_collisions > 0 && (
            <div className="badge warn" style={{ display: 'block' }}>
              ⚠ Обнаружено {committed.sku_collisions} коллизий артикула — товарам с одинаковым
              сгенерированным SKU добавлен суффикс, чтобы они не перезаписали друг друга. Проверь их в списке товаров.
            </div>
          )}
          {committed.status === 'error' && committed.inserted === 0 && committed.updated === 0 && (
            <div className="error">
              Импорт не создал ни одного товара (в файле {committed.total_input_rows} строк). Похоже, маппинг не подошёл —
              проверь колонки/тип таблицы и попробуй снова.
            </div>
          )}
          {committed.is_companion && committed.companion_merge && (
            <div className="card sub" style={{ marginTop: 8, background: '#eef3ec' }}>
              <strong>Merge с основным импортом:</strong>{' '}
              склеено <strong>{committed.companion_merge.merged}</strong>,
              неоднозначных <strong>{committed.companion_merge.ambiguous}</strong>,
              без пары <strong>{committed.companion_merge.unmatched}</strong>.
              <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                Каждая «склейка» обновляет недостающие поля у уже существующего товара (bunch_size, stock_qty, min_order_count). Существующие name / price у primary не перезаписываются.
                {committed.companion_merge.unmatched > 0 && ' Несовпавшие — сорта/высоты, которых нет в primary; они НЕ создаются как отдельные товары.'}
              </div>
            </div>
          )}
          {committed.errors.length > 0 && (
            <details>
              <summary>{committed.errors.length} ошибок</summary>
              <ul>
                {committed.errors.slice(0, 50).map((e, i) => (
                  <li key={i}>{e.name || '(?)'}: {e.reason}</li>
                ))}
              </ul>
            </details>
          )}
          <div className="row" style={{ marginTop: 12, gap: 8, flexWrap: 'wrap' }}>
            <Link to={withBase(base, '/products?status=draft')} className="btn">→ К черновикам</Link>
            <Link to={withBase(base, '/products')} className="btn">→ Ко всем товарам</Link>
            <button className="btn" onClick={() => resetUpload()}>Загрузить ещё файл</button>
            <button
              className="btn primary"
              onClick={() => {
                setCompanionOfId(committed.import_id);
                resetUpload({ keepCompanion: true });
              }}
              title="Например: к этому прайсу прислали отдельный файл «квант» — прикрепи его сейчас"
            >+ Прикрепить компаньон-файл</button>
          </div>
        </div>
      )}

      {/* Companion banner — shown only while operator is in the middle of
          uploading a second file linked to a prior import. */}
      {companionOfId != null && !committed && (
        <div className="badge ok" style={{ display: 'block', marginTop: 12, padding: 10 }}>
          ⛓ Этот файл будет прикреплён как компаньон к импорту #{companionOfId}.{' '}
          <button className="link-btn" type="button" onClick={() => setCompanionOfId(null)}>отвязать</button>
        </div>
      )}

      {/* --- Saved formats --- */}
      <div style={{ marginTop: 24 }}>
        <button
          className="link-btn"
          onClick={() => setShowFormats((v) => !v)}
          style={{ fontSize: 14, fontWeight: 600 }}
        >
          {showFormats ? '▾' : '▸'} Сохранённые форматы ({formats.length})
        </button>
        {showFormats && (
          <div style={{ marginTop: 8 }}>
            {formats.length === 0 ? (
              <p className="muted">Пусто. Сохрани формат после первой удачной загрузки — следующая такая же пройдёт без AI.</p>
            ) : (
              <table className="grid compact">
                <thead>
                  <tr>
                    <th>Название</th>
                    <th>Тип</th>
                    <th>Версия</th>
                    <th>Подпись</th>
                    <th>Образец</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {formats.map((f) => (
                    <tr key={f.id}>
                      <td>{f.name}{f.seller_id == null && <span className="muted"> (общий)</span>}</td>
                      <td><code>{f.table_type}</code></td>
                      <td>v{f.version}</td>
                      <td><code style={{ fontSize: 10 }}>{f.detector_signature.slice(0, 12)}</code></td>
                      <td className="muted" style={{ fontSize: 11 }}>{f.sample_filename || '—'}</td>
                      <td>
                        <button
                          className="btn"
                          type="button"
                          onClick={async () => {
                            if (!confirm(`Удалить формат «${f.name}»?`)) return;
                            await priceAiApi.formats.remove(f.id);
                            setFormats((arr) => arr.filter((x) => x.id !== f.id));
                          }}
                        >Удалить</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* --- Import history --- */}
      <div style={{ marginTop: 12 }}>
        <button
          className="link-btn"
          onClick={() => setShowHistory((v) => !v)}
          style={{ fontSize: 14, fontWeight: 600 }}
        >
          {showHistory ? '▾' : '▸'} Журнал импортов ({history.length})
        </button>
        {showHistory && (
          <div style={{ marginTop: 8 }}>
            {history.length === 0 ? (
              <p className="muted">Пусто.</p>
            ) : (
              <table className="grid compact">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Дата</th>
                    <th>Файл</th>
                    <th>Статус</th>
                    <th>Создано</th>
                    <th>Обновлено</th>
                    <th>Пропущено</th>
                    <th>Ошибки</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id}>
                      <td>{h.id}</td>
                      <td>{h.uploaded_at?.slice(0, 19).replace('T', ' ')}</td>
                      <td>{h.original_filename ?? '—'}</td>
                      <td><span className={`badge ${h.status === 'success' ? 'ok' : h.status === 'rolled_back' ? '' : 'warn'}`}>{h.status}</span></td>
                      <td>{h.rows_inserted}</td>
                      <td>{h.rows_updated}</td>
                      <td>{h.rows_skipped}</td>
                      <td>{h.errors?.length ?? 0}</td>
                      <td>
                        {h.status !== 'rolled_back' && (h.rows_inserted > 0 || h.rows_updated > 0) && (
                          <button
                            type="button"
                            className="link-btn"
                            style={{ fontSize: 12, color: '#a52e22' }}
                            onClick={async () => {
                              if (!confirm(`Откатить импорт #${h.id}?\nСозданные товары будут удалены, изменённые — восстановлены к прежним значениям.`)) return;
                              try {
                                const r = await priceAiApi.rollback(h.id);
                                alert(`Откат выполнен: удалено ${r.deleted}, восстановлено ${r.restored}.`);
                                if (effectiveSellerId != null) {
                                  priceImportsApi.list(effectiveSellerId).then((x) => setHistory(x.imports)).catch(() => {});
                                }
                              } catch (e) {
                                alert(`Не удалось откатить: ${String((e as Error)?.message ?? e)}`);
                              }
                            }}
                          >Откатить</button>
                        )}
                        {(h.rows_inserted > 0 || h.rows_updated > 0) && (
                          <>
                            {h.status !== 'rolled_back' && <span style={{ margin: '0 6px', color: '#ccc' }}>·</span>}
                            <button
                              type="button"
                              className="link-btn"
                              style={{ fontSize: 12 }}
                              disabled={diffLoading === h.id}
                              onClick={() => openDiff(h.id)}
                            >{diffLoading === h.id ? 'Загрузка…' : 'Изменения'}</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Diff inspector — modal listing per-product field changes. */}
      {diffModal && (
        <div className="modal-overlay" onClick={() => setDiffModal(null)}>
          <div className="modal" style={{ maxWidth: 980, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-head" style={{ padding: '16px 22px', borderBottom: '1px solid var(--brand-line)', display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <h3 style={{ margin: 0 }}>Изменения импорта #{diffModal.import.id}</h3>
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                  {diffModal.import.original_filename} · {diffModal.import.uploaded_at?.slice(0, 19).replace('T', ' ')}
                  {' · '}
                  Всего: <strong>{diffModal.total_diffs}</strong>{' · '}
                  Новые товары: <strong>{diffModal.inserted_count}</strong>{' · '}
                  Обновлено: <strong>{diffModal.updated_count}</strong>
                </div>
              </div>
              <button className="btn" type="button" onClick={() => setDiffModal(null)}>Закрыть</button>
            </div>
            <div style={{ overflow: 'auto', padding: '16px 22px' }}>
              {diffModal.groups.length === 0 ? (
                <p className="muted">Изменений нет.</p>
              ) : (
                <table className="grid compact">
                  <thead>
                    <tr>
                      <th>Товар</th>
                      <th>Поле</th>
                      <th>Было</th>
                      <th>Стало</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diffModal.groups.flatMap((g) => {
                      if (g.is_inserted) {
                        return [
                          <tr key={`g${g.product_id}-ins`} style={{ background: '#eef3ec' }}>
                            <td>
                              <strong>{g.name}</strong>
                              {g.external_offer_id && <code style={{ fontSize: 10, marginLeft: 6 }}>{g.external_offer_id}</code>}
                            </td>
                            <td colSpan={3}>
                              <span className="badge ok">＋ новый товар</span>
                            </td>
                          </tr>,
                        ];
                      }
                      return g.fields.map((f, i) => (
                        <tr key={`g${g.product_id}-f${i}`}>
                          {i === 0 ? (
                            <td rowSpan={g.fields.length} style={{ verticalAlign: 'top' }}>
                              <strong>{g.name}</strong>
                              {g.external_offer_id && <div><code style={{ fontSize: 10 }}>{g.external_offer_id}</code></div>}
                            </td>
                          ) : null}
                          <td>{DIFF_FIELD_LABELS[f.field] || f.field}</td>
                          <td><span style={{ color: '#a52e22' }}>{shortenValue(f.old)}</span></td>
                          <td><span style={{ color: '#1f6f3e' }}>{shortenValue(f.new)}</span></td>
                        </tr>
                      ));
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const DIFF_FIELD_LABELS: Record<string, string> = {
  name:            'Название',
  price_rub:       'Цена, ₽',
  bunch_size:      'Кол-во в упаковке',
  min_order_count: 'Минимум',
  stock_qty:       'Наличие',
  properties_json: 'Свойства (высота и пр.)',
  images_json:     'Фото',
  description:     'Описание',
};

function shortenValue(v: string | null): string {
  if (v == null || v === '') return '—';
  if (v.length > 80) return v.slice(0, 77) + '…';
  return v;
}

// Build the rules object that /commit-ai expects from analyzer output +
// operator's defaults/overrides. Anything the operator entered as a default
// gets a literal source.
function buildRules(
  r: AnalyzeResponse,
  overrides: Record<string, { col?: number; value?: number | string }>,
  defaults: Record<string, number | string>,
): Record<string, unknown> {
  const a = r.analysis;
  const d = r.detector;
  const mapping: Record<string, unknown> = {};
  // Seed from AI analysis if present, else from detector field_guesses.
  const seed = a?.mapping ?? Object.fromEntries(
    Object.entries(d.field_guesses).map(([f, g]) => [f, { source: 'column', col: g.col, confidence: g.confidence }]),
  );
  for (const [f, m] of Object.entries(seed)) {
    mapping[f] = { ...(m as object), ...(overrides[f] || {}) };
  }
  // Convert UI-entered defaults into literal mappings, but ONLY if not
  // already covered by a column source. This is what fills in min=1, etc.
  const defaultsClean: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(defaults)) {
    if (v === '' || v == null) continue;
    if (!mapping[k] || (mapping[k] as { col?: number }).col == null) {
      mapping[k] = { source: 'literal', value: v };
    }
    defaultsClean[k] = v;
  }

  const rules: Record<string, unknown> = {
    table_type: a?.table_type ?? d.table_type,
    sheet_index: d.sheet_index,
    header_row: d.header_row,
    data_start_row: d.data_start_row,
    mapping,
    section_rule: a?.section_rule ?? (d.section_count > 0 ? { use_section_as: 'category', column: d.variety_column, match: 'merged_or_single_text_row' } : null),
    unpivot: a?.unpivot ?? (d.table_type === 'pivot' && d.variety_column != null ? {
      variety_col: d.variety_column,
      value_cols: d.value_columns,
      value_field: 'price_rub',
      size_label_to_height_cm: true,
    } : null),
    defaults: defaultsClean,
  };
  return rules;
}

// Merge AI mapping + detector field_guesses into one per-field display row.
function mergeFieldGuesses(r: AnalyzeResponse | null) {
  if (!r) return {};
  const out: Record<string, { source: string; col?: number | null; value?: unknown; confidence?: number }> = {};
  const detector = r.detector;
  for (const [f, g] of Object.entries(detector.field_guesses)) {
    out[f] = { source: 'column', col: g.col, confidence: g.confidence };
  }
  if (r.analysis?.mapping) {
    for (const [f, m] of Object.entries(r.analysis.mapping)) {
      out[f] = {
        source: m.source,
        col: m.col,
        value: m.value,
        confidence: m.confidence ?? (out[f]?.confidence ?? 0.5),
      };
    }
  }
  // Ensure required fields are present as rows even if missing.
  for (const f of REQUIRED_FIELDS) {
    if (!out[f]) out[f] = { source: 'missing', col: null, confidence: 0 };
  }
  return out;
}

// Compare two AI proposals field-by-field. Per-field outcome:
//   "same"     — col / source / literal value all identical
//   "changed"  — same field present in both, but resolved differently
//   "new"      — field appears in the new proposal only
//   "removed"  — field was in the old proposal, not in the new one
function diffMappings(prev: AnalyzeResponse | null, curr: AnalyzeResponse | null): Record<string, 'same' | 'changed' | 'new' | 'removed'> {
  if (!prev || !curr) return {};
  const prevMap = prev.analysis?.mapping ?? {};
  const currMap = curr.analysis?.mapping ?? {};
  const out: Record<string, 'same' | 'changed' | 'new' | 'removed'> = {};
  const allKeys = new Set([...Object.keys(prevMap), ...Object.keys(currMap)]);
  for (const k of allKeys) {
    const p = prevMap[k];
    const c = currMap[k];
    if (p && !c) { out[k] = 'removed'; continue; }
    if (!p && c) { out[k] = 'new';     continue; }
    if (!p && !c) continue;
    if (p.source !== c.source || p.col !== c.col || p.value !== c.value || p.parser !== c.parser) {
      out[k] = 'changed';
    } else {
      out[k] = 'same';
    }
  }
  return out;
}

function diffWarnings(prev: string[] | null, curr: string[]): { added: string[]; removed: string[] } {
  if (!prev) return { added: [], removed: [] };
  const prevSet = new Set(prev);
  const currSet = new Set(curr);
  return {
    added:   curr.filter((w) => !prevSet.has(w)),
    removed: prev.filter((w) => !currSet.has(w)),
  };
}

function guessFormatName(filename: string): string {
  // Drop extension and trim long suffixes.
  return filename.replace(/\.(xlsx|xls|csv)$/i, '').slice(0, 60);
}
